import { ReactElement, useState } from 'react';
import Image, { StaticImageData } from 'next/image';
import clsx from 'clsx';

import Exclamation from '~/public/font-awesome/exclamation-triangle-brick.png';
import { Loader } from '~/components/ui';

export default function InfoCard({
  line1,
  line2,
  line3,
  isDefault,
  isExpired,
  onClickRemove,
  onClickPrimary,
  cardDetails,
  error,
  disabled,
}: Readonly<{
  line1?: string;
  line2?: string;
  line3?: string;
  isDefault?: boolean;
  isExpired?: boolean;
  onClickRemove: () => Promise<void>;
  onClickPrimary: (() => Promise<void>) | null;
  cardDetails?: {
    brand: string;
    icon: StaticImageData;
  };
  error?: string;
  disabled?: boolean;
}>): ReactElement {
  const [isRemoveLoading, setIsRemoveLoading] = useState(false);
  const [isPrimaryLoading, setIsPrimaryLoading] = useState(false);
  const lineClassName = clsx(cardDetails ? 'text-grace' : 'mt-2');
  const thumbnailClassName = clsx(isExpired && 'opacity-50');
  const lineItemsContainerClassName = clsx('grow', thumbnailClassName);
  const containerClass = clsx(
    'flex rounded border p-4',
    error && 'border-brick',
  );

  const onClickPrimaryButton = () => {
    if (!onClickPrimary) return;

    setIsPrimaryLoading(true);
    onClickPrimary().finally(() => {
      setIsPrimaryLoading(false);
    });
  };

  const onClickRemoveButton = () => {
    setIsRemoveLoading(true);
    onClickRemove().finally(() => {
      setIsRemoveLoading(false);
    });
  };

  const disableBtn = disabled || isRemoveLoading || isPrimaryLoading;
  const primaryElClass = clsx('mr-4 flex', disableBtn && 'text-gray-400');
  const removeElClass = clsx('flex', disableBtn && 'text-gray-400');
  const line1El = line1 ? <div className="font-bold">{line1}</div> : null;
  const line2El = line2 ? <div className={lineClassName}>{line2}</div> : null;
  const line3El = line3 ? <div className={lineClassName}>{line3}</div> : null;
  const thumbnailEl = cardDetails ? (
    <div className={thumbnailClassName}>
      <Image
        src={cardDetails.icon}
        alt={cardDetails.brand}
        width={30}
        className="mr-2"
      />
    </div>
  ) : null;

  let statusEl = null;
  if (isExpired) {
    statusEl = (
      <span className="rounded bg-brick-light py-1 px-2 font-medium text-brick">
        Expired
      </span>
    );
  } else if (isDefault) {
    statusEl = (
      <span className="rounded bg-[#E9F2FF] py-1 px-2 font-medium">
        Default
      </span>
    );
  }

  let onClickDefaultEl = null;
  if (onClickPrimary && !isExpired) {
    let primaryLabel = <>Edit</>;
    if (cardDetails) {
      primaryLabel = (
        <>
          Make default{' '}
          {isPrimaryLoading && <Loader size="sm" className="ml-1" />}
        </>
      );
    }

    onClickDefaultEl = (
      <button
        className={primaryElClass}
        onClick={onClickPrimaryButton}
        onKeyDown={onClickPrimaryButton}
        disabled={disableBtn}
        data-testid="info-card-primary-button"
      >
        {primaryLabel}
      </button>
    );
  }

  return (
    <div className="my-4">
      <div className={containerClass}>
        {thumbnailEl}
        <div
          className={lineItemsContainerClassName}
          data-testid="info-card-line-items"
        >
          {line1El}
          {line2El}
          {line3El}
        </div>
        <div className="flex flex-col justify-between text-sm text-bolt">
          <div className="text-right">{statusEl}</div>
          <div className="mt-2 flex justify-end">
            {onClickDefaultEl}
            <button
              onClick={onClickRemoveButton}
              onKeyDown={onClickRemoveButton}
              disabled={disableBtn}
              className={removeElClass}
              data-testid="info-card-remove-button"
            >
              Remove{' '}
              {isRemoveLoading ? <Loader size="sm" className="ml-1" /> : null}
            </button>
          </div>
        </div>
      </div>
      {error && (
        <div className="mt-2 flex items-center text-brick">
          <div>
            <Image
              src={Exclamation}
              alt="Exclamation"
              width={18}
              height={18}
              className="mr-2"
            />
          </div>
          {error}
        </div>
      )}
    </div>
  );
}
